import { defineStore } from 'pinia'
import { ThymeFeatureFlagClient } from './client'
import {
  FeatureFlagProviderConfig,
  FeatureFlagTargetingConfig,
  FeatureFlagValues,
  FlagDefinitions,
} from './types'

type FlagState<T extends FlagDefinitions> = {
  client: ThymeFeatureFlagClient<T> | null
  isLoaded: boolean
  error: Error | null
} & FeatureFlagValues<T>

/**
 * Creates a Pinia store for managing feature flags using OpenFeature.
 *
 * The initial values will be the defined fallback values for the flags.
 * To use real values from the feature flag provider, the `init` method must be called
 * with a user key and provider. If the user key changes, it is safe
 * to call `init` again with the new key. Once initialized, the store will
 * automatically update the flag values when the provider sends new values.
 * @param flagDefinitions
 */
export function createFlagStore<T extends FlagDefinitions>(flagDefinitions: T) {
  return defineStore('flags', {
    state: (): FlagState<T> => ({
      client: null,
      isLoaded: false,
      error: null,
      ...ThymeFeatureFlagClient.getFallbackValues(flagDefinitions),
    }),
    actions: {
      async init(
        providerConfig: FeatureFlagProviderConfig,
        targetingConfig: FeatureFlagTargetingConfig
      ) {
        console.debug('Initializing OpenFeature client...')
        console.debug('targetingKey:', targetingConfig.targetingKey)
        await this.destroy()

        // Get a client instance
        const client = await ThymeFeatureFlagClient.createOpenFeatureClient(
          flagDefinitions,
          providerConfig,
          targetingConfig
        )

        // Note: quite possibly due to the generics, `this` is typed
        // incorrectly in the store, so we have had to ignore throughout.
        // @ts-ignore
        this.client = client

        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
        this.client.onValuesUpdated(() => {
          void this.syncFlags()
        })

        try {
          await this.syncFlags()
          // @ts-ignore
          this.isLoaded = true
          // @ts-ignore
          this.error = null
        } catch (err) {
          console.error('Error initializing OpenFeature client:', err)
          // @ts-ignore
          this.error = err instanceof Error ? err : new Error(String(err))
          // Still mark as loaded so app doesn't hang forever - will use fallback values
          // @ts-ignore
          this.isLoaded = true
        }
      },

      async destroy() {
        // @ts-ignore
        await this.client?.destroy()
        // @ts-ignore
        this.client = null
        // @ts-ignore
        this.error = null
      },

      async syncFlags() {
        console.log('Syncing flags with provider...')
        // @ts-ignore
        if (!this.client) {
          console.error('Cannot sync flags. Client is not initialized')
          return
        }

        // @ts-ignore
        Object.assign(this.$state, await this.client.getFlagValues())
      },
    },
  })
}
